import { ACCESS_LEVEL_RANK, AccessLevel, IArea, PermissionGrants, parentArea } from 'app/models/permission.model';

/** Choice for one area in the role editor. 'Inherit' (sub-areas only) means: same as the parent area. */
export type AreaChoice = AccessLevel | 'Inherit';

/** One row of the permission table */
export interface AreaRow {
  area: IArea;
  depth: number;
  hasParent: boolean;
}

/** Icons a custom role can have (Font Awesome, as elsewhere in the Portal) */
export const ROLE_ICONS: string[] = [
  'fa-user', 'fa-user-nurse', 'fa-user-doctor', 'fa-user-tie', 'fa-user-gear', 'fa-user-shield',
  'fa-user-clock', 'fa-eye', 'fa-house-medical', 'fa-screwdriver-wrench', 'fa-headset', 'fa-clipboard-list'
];

/** Table rows in registry order, children indented under their parent */
export function toRows(areas: IArea[]): AreaRow[] {
  return areas.map(area => ({
    area,
    depth: area.key.split('.').length - 1,
    hasParent: !!area.parentKey
  }));
}

/** Editor choices from stored grants: stated keys keep their level, unstated sub-areas inherit */
export function toChoices(areas: IArea[], grants: PermissionGrants): Record<string, AreaChoice> {
  const choices: Record<string, AreaChoice> = {};
  for (const area of areas) {
    choices[area.key] = grants[area.key] ?? (area.parentKey ? 'Inherit' : 'None');
  }
  return choices;
}

/**
 * Grants to store from editor choices. Top-level None is left out (the same as not granted);
 * a sub-area is stored only when it differs from its parent on purpose (not 'Inherit').
 */
export function toGrants(areas: IArea[], choices: Record<string, AreaChoice>): PermissionGrants {
  const grants: PermissionGrants = {};
  for (const area of areas) {
    const choice = choices[area.key];
    if (!choice || choice === 'Inherit') continue;
    if (!area.parentKey && choice === 'None') continue;
    grants[area.key] = choice;
  }
  return grants;
}

/** Level an area ends up with from the editor choices, following 'Inherit' up to the parent */
export function effectiveChoice(key: string, choices: Record<string, AreaChoice>): AccessLevel {
  for (let k: string | null = key; k !== null; k = parentArea(k)) {
    const choice = choices[k];
    if (choice && choice !== 'Inherit') return choice;
  }
  return 'None';
}

/**
 * Area whose page a sub-area lives on: the nearest ancestor that is a main area or has a page of its
 * own. Null for main areas and for sub-areas with their own page (e.g. Reports.Statistics), which
 * work without their parent.
 */
export function pageAreaOf(key: string, ownPageAreas: ReadonlySet<string>): string | null {
  if (!parentArea(key) || ownPageAreas.has(key)) return null;
  let page = parentArea(key);
  while (parentArea(page) && !ownPageAreas.has(page)) page = parentArea(page);
  return page;
}

/**
 * A sub-area that gives access its user can't reach, because the page it lives on is not accessible
 * in this role: the key of that page's area, otherwise null. Only a hint: saving is not blocked.
 */
export function unreachableFrom(key: string, choices: Record<string, AreaChoice>, ownPageAreas: ReadonlySet<string>): string | null {
  const page = pageAreaOf(key, ownPageAreas);
  if (!page || effectiveChoice(key, choices) === 'None') return null;
  return effectiveChoice(page, choices) === 'None' ? page : null;
}

/** Number of areas that end up with at least the level */
export function countAtLeast(areas: IArea[], choices: Record<string, AreaChoice>, level: AccessLevel): number {
  return areas.filter(a => ACCESS_LEVEL_RANK[effectiveChoice(a.key, choices)] >= ACCESS_LEVEL_RANK[level]).length;
}
